import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Checkbox, Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { powerAddonsAPI, powerAddonOptionsAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { PowerAddon, PowerAddonOption } from '../../types';

interface PowerAddonOptionFormData {
  PowerAddonId: number | string;
  OptionName: string;
  Description: string;
  DisplayOrder: number;
  IsActive: boolean;
}

const PowerAddonOptions = () => {
  const [powerAddons, setPowerAddons] = useState<PowerAddon[]>([]);
  const [powerAddonOptions, setPowerAddonOptions] = useState<PowerAddonOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<PowerAddonOption[]>([]);
  const [selectedPowerAddon, setSelectedPowerAddon] = useState<number | string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<PowerAddonOption | null>(null);
  const [formData, setFormData] = useState<PowerAddonOptionFormData>({ PowerAddonId: '', OptionName: '', Description: '', DisplayOrder: 1, IsActive: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchPowerAddons(); fetchPowerAddonOptions(); }, []);

  useEffect(() => {
    if (selectedPowerAddon) {
      const filtered = powerAddonOptions.filter((opt) => opt.PowerAddonId === Number(selectedPowerAddon));
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(powerAddonOptions);
    }
  }, [selectedPowerAddon, powerAddonOptions]);

  const fetchPowerAddons = async () => {
    try { const response = await powerAddonsAPI.getAll(); setPowerAddons(response.data.filter((a: PowerAddon) => a.IsActive)); }
    catch (err) { setError('Failed to load power addons'); }
  };

  const fetchPowerAddonOptions = async () => {
    try { const response = await powerAddonOptionsAPI.getAll(); setPowerAddonOptions(response.data); }
    catch (err) { setError('Failed to load power addon options'); }
  };

  const handleOpenDialog = (option: PowerAddonOption | null = null) => {
    if (option) {
      setEditingOption(option);
      setFormData({ PowerAddonId: option.PowerAddonId, OptionName: option.OptionName, Description: option.Description || '', DisplayOrder: option.DisplayOrder, IsActive: !!option.IsActive });
    } else {
      setEditingOption(null);
      setFormData({ PowerAddonId: selectedPowerAddon || '', OptionName: '', Description: '', DisplayOrder: 1, IsActive: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setEditingOption(null); setFormData({ PowerAddonId: '', OptionName: '', Description: '', DisplayOrder: 1, IsActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingOption) {
        await powerAddonOptionsAPI.update(editingOption.PowerAddonOptionId, { optionName: formData.OptionName, description: formData.Description, displayOrder: formData.DisplayOrder, isActive: formData.IsActive });
        setSuccess('Power addon option updated successfully');
      } else {
        await powerAddonOptionsAPI.create({ powerAddonId: formData.PowerAddonId, optionName: formData.OptionName, description: formData.Description, displayOrder: formData.DisplayOrder });
        setSuccess('Power addon option created successfully');
      }
      handleCloseDialog(); fetchPowerAddonOptions(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to save power addon option'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await powerAddonOptionsAPI.delete(id); setSuccess('Power addon option deleted successfully'); fetchPowerAddonOptions(); setTimeout(() => setSuccess(''), 3000); }
    catch (err) { setError('Failed to delete power addon option'); }
  };

  const getPowerAddonName = (powerAddonId: number) => {
    const powerAddon = powerAddons.find((a) => a.PowerAddonId === powerAddonId);
    return powerAddon ? powerAddon.PowerAddonName : 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Power Addon Options</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Add Power Addon Option</Button>
      </Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField select label="Filter by Power Addon" value={selectedPowerAddon} onChange={(e) => setSelectedPowerAddon(e.target.value)} fullWidth size="small">
          <MenuItem value="">All Power Addons</MenuItem>
          {powerAddons.map((powerAddon) => (<MenuItem key={powerAddon.PowerAddonId} value={powerAddon.PowerAddonId}>{powerAddon.PowerAddonName}</MenuItem>))}
        </TextField>
      </Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead><TableRow>
            <TableCell>Power Addon</TableCell><TableCell>Option Name</TableCell><TableCell>Description</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {filteredOptions.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No power addon options found</TableCell></TableRow>
            ) : (
              filteredOptions.map((option) => (
                <TableRow key={option.PowerAddonOptionId}>
                  <TableCell>{getPowerAddonName(option.PowerAddonId)}</TableCell><TableCell>{option.OptionName}</TableCell>
                  <TableCell>{option.Description || '-'}</TableCell><TableCell>{option.DisplayOrder}</TableCell>
                  <TableCell><Checkbox checked={!!option.IsActive} disabled /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(option)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(option.PowerAddonOptionId)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOption ? 'Edit Power Addon Option' : 'Add Power Addon Option'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField select label="Power Addon" value={formData.PowerAddonId} onChange={(e) => setFormData({ ...formData, PowerAddonId: e.target.value })} required fullWidth>
              {powerAddons.map((powerAddon) => (<MenuItem key={powerAddon.PowerAddonId} value={powerAddon.PowerAddonId}>{powerAddon.PowerAddonName}</MenuItem>))}
            </TextField>
            <TextField label="Option Name" value={formData.OptionName} onChange={(e) => setFormData({ ...formData, OptionName: e.target.value })} required fullWidth />
            <TextField label="Description" value={formData.Description} onChange={(e) => setFormData({ ...formData, Description: e.target.value })} multiline rows={3} fullWidth />
            <TextField label="Display Order" type="number" value={formData.DisplayOrder} onChange={(e) => setFormData({ ...formData, DisplayOrder: parseInt(e.target.value) })} required fullWidth />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox checked={formData.IsActive} onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })} />
              <Typography>Active</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.PowerAddonId || !formData.OptionName}>{editingOption ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Power Addon Option"
        message="Are you sure you want to delete this power addon option?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default PowerAddonOptions;
